<main class="marketplace-page py-5">
  <div class="container marketplace-container">
    <div class="marketplace-stepper d-flex flex-column flex-md-row gap-3 justify-content-between p-3 mb-4 shadow-sm">
      <div class="marketplace-step d-flex align-items-center gap-2" :class="{ 'is-active': is_step_active(1) }">
        <span class="marketplace-step-number d-inline-flex align-items-center justify-content-center">1</span>
        <span>Sign in with Microsoft</span>
      </div>
      <div class="marketplace-step d-flex align-items-center gap-2" :class="{ 'is-active': is_step_active(2) }">
        <span class="marketplace-step-number d-inline-flex align-items-center justify-content-center">2</span>
        <span>Confirm setup</span>
      </div>
      <div class="marketplace-step d-flex align-items-center gap-2">
        <span class="marketplace-step-number d-inline-flex align-items-center justify-content-center">3</span>
        <span>Setup in progress</span>
      </div>
    </div>

    <section class="marketplace-header mb-4 text-center">
      <p class="marketplace-eyebrow text-uppercase fw-bold mb-2">Microsoft Marketplace</p>
      <h1 class="marketplace-title fw-bold mb-3">
        {{ current_step === 2 ? 'Confirm subscription setup request' : 'Sign in with Microsoft' }}
      </h1>
      <p class="marketplace-subtitle text-muted text-center mx-auto">
        {{ current_step === 2
          ? 'Review your subscription and account details. Once submitted, our team will begin preparing your AIC One environment.'
          : 'Sign in using the Microsoft account associated with your Marketplace subscription to continue setup.' }}
      </p>
    </section>

    <div v-if="error_message" class="alert alert-danger border-0 shadow-sm" role="alert">
      {{ error_message }}
    </div>

    <div v-if="validation_message" class="alert alert-danger border-0 shadow-sm" role="alert">
      {{ validation_message }}
    </div>

    <div v-if="loading_details" class="marketplace-card card border-0 shadow-sm">
      <div class="card-body text-center p-5">
        <div class="spinner-border marketplace-spinner mb-3" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted mb-0">Loading subscription details...</p>
      </div>
    </div>

    <div v-else-if="activation_details" class="row g-4">
      <div class="col-lg-6">
        <section class="marketplace-card card border-0 shadow-sm h-100">
          <div class="card-body p-4 p-lg-5">
            <div class="d-flex align-items-start justify-content-between gap-3 mb-4">
              <div>
                <p class="marketplace-card-kicker text-uppercase fw-bold mb-2">Subscription summary</p>
                <h2 class="h4 fw-bold mb-0">Marketplace subscription</h2>
              </div>
              <span class="badge rounded-pill px-3 py-2" :class="status_badge_class()">
                {{ status_display_label() }}
              </span>
            </div>

            <div class="marketplace-detail-list border rounded-3 overflow-hidden">
              <div class="marketplace-detail-row row g-0 align-items-center">
                <div class="col-sm-5 marketplace-detail-label">Subscription name</div>
                <div class="col-sm-7 marketplace-detail-value text-break">{{ safe_display_value(subscription_name) }}</div>
              </div>
              <div class="marketplace-detail-row row g-0 align-items-center">
                <div class="col-sm-5 marketplace-detail-label">Offer ID</div>
                <div class="col-sm-7 marketplace-detail-value text-break">{{ safe_display_value(offer_id) }}</div>
              </div>
              <div class="marketplace-detail-row row g-0 align-items-center">
                <div class="col-sm-5 marketplace-detail-label">Plan ID</div>
                <div class="col-sm-7 marketplace-detail-value text-break">{{ safe_display_value(plan_id) }}</div>
              </div>
              <div class="marketplace-detail-row row g-0 align-items-center">
                <div class="col-sm-5 marketplace-detail-label">Status</div>
                <div class="col-sm-7 marketplace-detail-value text-break">{{ status_display_label() }}</div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="col-lg-6">
        <section class="marketplace-card card border-0 shadow-sm h-100">
          <div class="card-body p-4 p-lg-5 d-flex flex-column">
            <template v-if="current_step === 1">
              <p class="marketplace-card-kicker text-uppercase fw-bold mb-2">Step 1 of 3</p>
              <h2 class="h4 fw-bold mb-3">Sign in with Microsoft</h2>
              <p class="text-muted mb-4">
                Sign in using the Microsoft account associated with your Marketplace subscription to continue setup.
              </p>

              <div v-if="loading_context" class="text-center p-4">
                <div class="spinner-border marketplace-spinner mb-3" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="text-muted mb-0">Checking your Microsoft Marketplace account details...</p>
              </div>

              <div class="d-grid gap-2">
                <button type="button" class="btn marketplace-microsoft-btn fw-bold" @click="sign_in_with_microsoft">
                  <span class="marketplace-ms-icon" aria-hidden="true">
                    <span class="ms-red"></span>
                    <span class="ms-green"></span>
                    <span class="ms-blue"></span>
                    <span class="ms-yellow"></span>
                  </span>
                  Sign in with Microsoft
                </button>
                <button type="button" class="btn btn-outline-secondary fw-bold" @click="sign_up_with_microsoft">
                  Sign up with Microsoft
                </button>
              </div>
            </template>

            <template v-else-if="current_step === 2">
              <p class="marketplace-card-kicker text-uppercase fw-bold mb-2">Step 2 of 3</p>
              <h2 class="h4 fw-bold mb-3">Confirm subscription setup request</h2>

              <div v-if="loading_context" class="text-center p-4">
                <div class="spinner-border marketplace-spinner mb-3" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="text-muted mb-0">Loading Microsoft account details...</p>
              </div>

              <template v-else>
                <h3 class="h6 fw-bold mb-3">Microsoft account details</h3>
                <div class="marketplace-detail-list border rounded-3 overflow-hidden mb-4">
                  <div class="marketplace-detail-row row g-0 align-items-center">
                    <div class="col-sm-5 marketplace-detail-label">Name</div>
                    <div class="col-sm-7 marketplace-detail-value text-break">{{ safe_display_value(buyer_name) }}</div>
                  </div>
                  <div class="marketplace-detail-row row g-0 align-items-center">
                    <div class="col-sm-5 marketplace-detail-label">Email address</div>
                    <div class="col-sm-7 marketplace-detail-value text-break">{{ safe_display_value(buyer_email) }}</div>
                  </div>
                  <div class="marketplace-detail-row row g-0 align-items-center">
                    <div class="col-sm-5 marketplace-detail-label">Organisation / company</div>
                    <div class="col-sm-7 marketplace-detail-value text-break">{{ organization_display_value() }}</div>
                  </div>
                </div>

                <div class="form-check marketplace-confirm-check rounded-3 border p-3 ps-5 mb-4">
                  <input
                    id="setup_confirmed"
                    v-model="authorized_to_proceed"
                    class="form-check-input"
                    type="checkbox"
                  />
                  <label class="form-check-label fw-semibold" for="setup_confirmed">
                    I confirm that I am authorised to request setup of this Microsoft Marketplace subscription.
                  </label>
                </div>

                <div class="marketplace-actions mt-auto pt-4 border-top">
                  <p class="small text-muted mb-3">
                    Our team will prepare your environment within 24-48 hours after this request is received.
                  </p>
                  <div class="d-grid gap-2 d-sm-flex justify-content-sm-end">
                    <button type="button" class="btn btn-outline-secondary px-4" @click="go_to_login">
                      Return to login
                    </button>
                    <button
                      type="button"
                      class="btn marketplace-primary-btn px-4"
                      :disabled="submitting || !authorized_to_proceed"
                      @click="submit_setup_request"
                    >
                      <span
                        v-if="submitting"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {{ submitting ? 'Submitting...' : 'Submit setup request' }}
                    </button>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </section>
      </div>
    </div>
  </div>
</main>
